/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import React, { useEffect, useRef } from 'react';
import { Callout } from '@radix-ui/themes';
import { Info } from 'lucide-react';

import { useConfigureBaseUrlStep } from './useConfigureBaseUrlStep';
import UIStrings from './UIStrings';

import './ConfigureBaseUrlStep.scss';

export default function ConfigureBaseUrlStep(): JSX.Element {
  const {
    acceptReverseProxyHeaders,
    configureBaseUrl,
    host,
    force,
    likelyBehindReverseProxy,
    onToggleAcceptReverseProxyHeaders,
    onToggleConfigureBaseUrl,
    onHostChange,
    onToggleForce,
    onFormSubmit,
  } = useConfigureBaseUrlStep();

  const firstCheckboxRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    firstCheckboxRef.current?.focus();
  }, []);

  const {
    TITLE,
    SUBTITLE,
    ACCEPT_HEADERS_LABEL,
    ACCEPT_HEADERS_DESCRIPTION,
    LIKELY_BEHIND_PROXY_HINT,
    CONFIGURE_BASE_URL_LABEL,
    HOST_LABEL,
    HOST_PLACEHOLDER,
    FORCE_LABEL,
    FORCE_DESCRIPTION,
    DESCRIPTION,
    MORE_INFO_LABEL,
    MORE_INFO_HREF,
  } = UIStrings.ONBOARDING_WIZARD.CONFIGURE_BASE_URL;

  return (
    <div className="onboarding-wizard__configure-base-url" data-testid="onboarding-wizard__configure-base-url">
      <form onSubmit={onFormSubmit} className="onboarding-wizard__configure-base-url-form">
        <fieldset
          className="onboarding-wizard__configure-base-url-fieldset"
          aria-describedby="onboarding-wizard__configure-base-url-description"
        >
          <legend className="onboarding-wizard__configure-base-url-legend">{TITLE}</legend>
          <p className="onboarding-wizard__configure-base-url-subtitle">{SUBTITLE}</p>
          <Callout.Root
            id="onboarding-wizard__configure-base-url-description"
            className="onboarding-wizard__configure-base-url-description"
            data-testid="onboarding-wizard__configure-base-url-description-callout"
            color="blue"
            variant="surface"
          >
            <Callout.Icon>
              <Info size={16} />
            </Callout.Icon>
            <Callout.Text>
              {DESCRIPTION}{' '}
              <a
                href={MORE_INFO_HREF}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="onboarding-wizard__configure-base-url-more-info-link"
              >
                {MORE_INFO_LABEL}
              </a>
            </Callout.Text>
          </Callout.Root>

          <label
            htmlFor="onboarding-wizard__accept-reverse-proxy-headers"
            className="onboarding-wizard__configure-base-url-option"
          >
            <input
              id="onboarding-wizard__accept-reverse-proxy-headers"
              type="checkbox"
              checked={acceptReverseProxyHeaders}
              onChange={(e) => onToggleAcceptReverseProxyHeaders(e.target.checked)}
              ref={firstCheckboxRef}
              data-testid="onboarding-wizard__accept-reverse-proxy-headers"
              className="onboarding-wizard__configure-base-url-option-input"
            />
            <span className="onboarding-wizard__configure-base-url-option-content">
              <span className="onboarding-wizard__configure-base-url-option-title">{ACCEPT_HEADERS_LABEL}</span>
              <span className="onboarding-wizard__configure-base-url-option-description">
                {ACCEPT_HEADERS_DESCRIPTION}
              </span>
              {likelyBehindReverseProxy && (
                <span
                  className="onboarding-wizard__configure-base-url-hint"
                  data-testid="onboarding-wizard__likely-behind-proxy-hint"
                >
                  {LIKELY_BEHIND_PROXY_HINT}
                </span>
              )}
            </span>
          </label>

          <label
            htmlFor="onboarding-wizard__configure-base-url-toggle"
            className="onboarding-wizard__configure-base-url-option"
          >
            <input
              id="onboarding-wizard__configure-base-url-toggle"
              type="checkbox"
              checked={configureBaseUrl}
              onChange={(e) => onToggleConfigureBaseUrl(e.target.checked)}
              data-testid="onboarding-wizard__configure-base-url-toggle"
              className="onboarding-wizard__configure-base-url-option-input"
            />
            <span className="onboarding-wizard__configure-base-url-option-content">
              <span className="onboarding-wizard__configure-base-url-option-title">
                {CONFIGURE_BASE_URL_LABEL}
              </span>
            </span>
          </label>

          {configureBaseUrl && (
            <div className="onboarding-wizard__configure-base-url-subsection">
              <label
                htmlFor="onboarding-wizard__base-url-host"
                className="onboarding-wizard__configure-base-url-field-label"
              >
                {HOST_LABEL}
              </label>
              <input
                id="onboarding-wizard__base-url-host"
                type="text"
                value={host}
                placeholder={HOST_PLACEHOLDER}
                onChange={(e) => onHostChange(e.target.value)}
                data-testid="onboarding-wizard__base-url-host"
                className="onboarding-wizard__configure-base-url-field-input"
              />

              <label
                htmlFor="onboarding-wizard__base-url-force"
                className="onboarding-wizard__configure-base-url-option"
              >
                <input
                  id="onboarding-wizard__base-url-force"
                  type="checkbox"
                  checked={force}
                  onChange={(e) => onToggleForce(e.target.checked)}
                  data-testid="onboarding-wizard__base-url-force"
                  className="onboarding-wizard__configure-base-url-option-input"
                />
                <span className="onboarding-wizard__configure-base-url-option-content">
                  <span className="onboarding-wizard__configure-base-url-option-title">{FORCE_LABEL}</span>
                  <span className="onboarding-wizard__configure-base-url-option-description">
                    {FORCE_DESCRIPTION}
                  </span>
                </span>
              </label>
            </div>
          )}
        </fieldset>
        <button
          type="submit"
          className="onboarding-wizard__configure-base-url-hidden-submit"
          aria-hidden="true"
          tabIndex={-1}
        />
      </form>
    </div>
  );
}
